import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Icon, IconName } from '../ui/Icon';
import { MosyMark } from '../ui/MosyMark';
import CreateBrandModal from '../modals/CreateBrandModal';
import CreateCampaignModal from '../modals/CreateCampaignModal';

interface NavEntry {
  to: string;
  icon: IconName;
  label: string;
  end?: boolean;
  /** Custom active rule when sibling items share the same path prefix. */
  activeWhen?: (path: string) => boolean;
  /** Indented sub-item under the entry above it. */
  sub?: boolean;
}

// Temporary shortcuts for the 2026 Black Week season (Robbie, 2026-10-28).
// Remove once Black Week is over.
const BLACK_WEEK = '/campaigns/c-black-week';

interface NavGroup {
  label?: string;
  items: NavEntry[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
      { to: '/', icon: 'dashboard', label: 'Dashboard', end: true },
      { to: '/my-work', icon: 'user', label: 'My work' },
      { to: '/tasks', icon: 'task', label: 'Tasks' },
    ],
  },
  {
    label: 'Workspaces',
    items: [
      { to: '/campaigns', icon: 'campaign', label: 'Campaigns', activeWhen: (p) => p.startsWith('/campaigns') && !p.startsWith(BLACK_WEEK) },
      { to: BLACK_WEEK, icon: 'flame', label: '🖤 Black Week', sub: true, activeWhen: (p) => p.startsWith(BLACK_WEEK) && !p.startsWith(`${BLACK_WEEK}/partnership`) },
      { to: `${BLACK_WEEK}/partnership`, icon: 'link', label: 'Partnership page', sub: true },
      { to: '/brands', icon: 'brand', label: 'Brands' },
    ],
  },
  {
    label: 'Planning',
    items: [
      { to: '/social', icon: 'instagram', label: 'Social' },
      { to: '/newsletters', icon: 'mail', label: 'Newsletters' },
      { to: '/homepage', icon: 'home', label: 'Homepage' },
      { to: '/shop-banners', icon: 'promoStrip', label: 'Shop banners & timers' },
    ],
  },
  {
    label: 'Black Friday',
    items: [
      { to: '/black-friday', icon: 'flame', label: 'Brands & terms', activeWhen: (p) => p.startsWith('/black-friday') && !/^\/black-friday\/(history|calendar)/.test(p) },
      { to: '/black-friday/history', icon: 'archive', label: 'History' },
      { to: '/black-friday/calendar', icon: 'calendar', label: 'Calendar' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/rate-card', icon: 'tag', label: 'Placements & prices' },
      { to: '/brand-kit', icon: 'sparkle', label: 'Brand kit' },
    ],
  },
];

interface SidebarProps {
  /** Drawer state on phones; ignored on wider screens. */
  open?: boolean;
  onClose?: () => void;
}

export const Sidebar = ({ open = false, onClose }: SidebarProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [brandOpen, setBrandOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);

  return (
    <>
      <aside className={`sidebar${open ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <div className="sidebar-logo">
            <MosyMark size={48} />
          </div>
          <div>
            <div className="sidebar-brand-name">mosy</div>
            <div className="sidebar-brand-org">Marketing Ops</div>
          </div>
          <button
            type="button"
            className="icon-btn sidebar-close"
            aria-label="Close menu"
            onClick={onClose}
          >
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="sidebar-shortcuts">
          <button
            type="button"
            className="btn primary sidebar-shortcut-btn"
            onClick={() => { onClose?.(); setCampaignOpen(true); }}
          >
            <Icon name="plus" size={13} /> New campaign
          </button>
          <button
            type="button"
            className="btn ghost sidebar-shortcut-btn"
            onClick={() => { onClose?.(); setBrandOpen(true); }}
          >
            <Icon name="plus" size={13} /> New brand
          </button>
        </div>

        {NAV_GROUPS.map((group, idx) => (
          <div key={group.label ?? `g-${idx}`} style={{ marginTop: idx === 0 ? 0 : 14 }}>
            {group.label && <div className="sidebar-section-label">{group.label}</div>}
            <nav className="sidebar-nav">
              {group.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={onClose}
                  className={({ isActive }) => `nav-item ${n.sub ? 'sub ' : ''}${(n.activeWhen ? n.activeWhen(pathname) : isActive) ? 'active' : ''}`}
                >
                  <Icon name={n.icon} size={15} />
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
        ))}

        {/* Quiet link at the bottom for the how-to guide — always
            visible so anyone new can find it. Auto-pushes to the
            bottom of the flex column. */}
        <nav
          className="sidebar-nav"
          style={{ marginTop: 'auto', paddingTop: 14, borderTop: '1px solid var(--border)' }}
        >
          <NavLink
            to="/how-to"
            onClick={onClose}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          >
            <Icon name="helpCircle" size={15} />
            How-to guide
          </NavLink>
        </nav>
      </aside>

      <CreateBrandModal
        open={brandOpen}
        onClose={() => setBrandOpen(false)}
        onCreated={(id) => navigate(`/brands/${id}`)}
      />
      <CreateCampaignModal
        open={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        onCreated={(id) => navigate(`/campaigns/${id}`)}
      />
    </>
  );
};

export default Sidebar;
